// Shared geometry helpers for positioning portaled UI (menus, tooltips, popovers)
// within the browser viewport. Consolidates the "flip and clamp to viewport" math
// that previously lived independently in the menu, Tooltip, and CitationBadge components.

// Default gap to keep between a portaled element and the viewport edge.
export const VIEWPORT_MARGIN = 8;

// Clamp a 1D coordinate so an element of `size` stays within
// [margin, viewportSize - margin]. When the element is larger than the available
// space, it pins to `margin`.
export function clampToViewport(pos: number, size: number, viewportSize: number, margin: number = VIEWPORT_MARGIN) {
  const max = viewportSize - size - margin;
  return Math.min(Math.max(pos, margin), Math.max(margin, max));
}

// Does an element of `size` fit before `edge` (above it / to its left) within the viewport?
export function fitsBefore(edge: number, size: number, margin: number = VIEWPORT_MARGIN) {
  return edge - size - margin >= 0;
}

// Does an element of `size` fit after `edge` (below it / to its right) within the viewport?
export function fitsAfter(edge: number, size: number, viewportSize: number, margin: number = VIEWPORT_MARGIN) {
  return edge + size + margin <= viewportSize;
}

// Where a portaled card opens beside its anchor. Below when `preferredHeight` fits there, otherwise on
// whichever side has more room. Above is given as a distance from the viewport's bottom edge, so a card
// whose content arrives after it opens (a PDF page rendering) grows away from the anchor instead of back
// over it. `maxHeight` is the room on the chosen side, for the card to scroll within rather than run
// off-screen.
export type VerticalPlacement =
  | { side: 'below'; top: number; maxHeight: number }
  | { side: 'above'; bottom: number; maxHeight: number };

export function placeVertically(
  anchor: { top: number; bottom: number },
  preferredHeight: number,
  viewportHeight: number,
  gap: number,
  margin: number = VIEWPORT_MARGIN
): VerticalPlacement {
  const spaceBelow = Math.max(0, viewportHeight - anchor.bottom - gap - margin);
  const spaceAbove = Math.max(0, anchor.top - gap - margin);
  if (spaceBelow >= preferredHeight || spaceBelow >= spaceAbove) {
    return { side: 'below', top: anchor.bottom + gap, maxHeight: spaceBelow };
  }
  return { side: 'above', bottom: viewportHeight - anchor.top + gap, maxHeight: spaceAbove };
}
